import { useMemo, useState } from 'react'
import { useCadenceFlowMetrics, type FlowStep } from '@/hooks/useCadenceFlowMetrics'
import { useOutreachEngineStatus } from '@/hooks/useOutreachEngineStatus'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertCircle, PauseCircle, Workflow, Zap, Hand } from 'lucide-react'
import { CadenceFlowStepCard } from './CadenceFlowStepCard'
import { CadenceFlowStepDialog } from './CadenceFlowStepDialog'
import { getStepTypeLabel } from './flowStepHelpers'

type Props = {
  cadenceId: string
  daysWindow?: number
}

function HowItWorks({ steps, cadenceName }: { steps: FlowStep[]; cadenceName: string }) {
  const summary = useMemo(() => {
    if (steps.length === 0) return ''
    const days = Array.from(new Set(steps.map((s) => s.day_offset))).sort((a, b) => a - b)
    const parts = days.map((d) => {
      const dayLabels = steps
        .filter((s) => s.day_offset === d)
        .map((s) => getStepTypeLabel(s.step_type))
        .join(' + ')
      return `Day ${d}: ${dayLabels}`
    })
    return parts.join(' → ')
  }, [steps])

  return (
    <Card className="border-dashed bg-muted/30 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Workflow className="size-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Cómo funciona “{cadenceName}”</h3>
          <p className="mt-1 text-xs text-muted-foreground">{summary || '—'}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Cada card de abajo muestra qué se ejecutó, qué falló y cómo está calificando Carlos (QA)
            ese touch. Click en una card para ver el detalle de los últimos leads ejecutados.
          </p>
        </div>
      </div>
    </Card>
  )
}

function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-20 animate-pulse rounded-md border border-dashed bg-muted/30" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-28 animate-pulse rounded-lg bg-muted/50" />
          {i < 4 && <div className="ml-5 h-4 w-px bg-border" />}
        </div>
      ))}
    </div>
  )
}

export function CadenceFlowTimeline({ cadenceId, daysWindow = 30 }: Props) {
  const { data, isLoading, isError, error } = useCadenceFlowMetrics(cadenceId, daysWindow)
  const { engineOn, engineOff } = useOutreachEngineStatus()
  const [openStepId, setOpenStepId] = useState<string | null>(null)

  // CA-055: total scheduled instances across the cadence. When the engine is
  // off and this cadence is automated, these are effectively overdue — they
  // will NOT execute until the engine is turned back on.
  const totalScheduled = useMemo(
    () => (data ? data.steps.reduce((sum, s) => sum + (s.metrics.scheduled || 0), 0) : 0),
    [data],
  )

  if (isLoading) return <TimelineSkeleton />
  if (isError) {
    return (
      <Card className="border-destructive/40 bg-destructive/5 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="size-5 text-destructive" />
          <div>
            <h3 className="font-semibold">No se pudieron cargar las métricas</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </div>
      </Card>
    )
  }
  if (!data) return null

  const openStep = data.steps.find((s) => s.step_id === openStepId) ?? null

  const isAutomated = data.cadence.automation_mode === 'automated'
  // A cadence looks "active" in DB, but with the engine off its scheduled steps
  // are a dead queue. Only call the queue LIVE when the engine is confirmed on.
  const scheduledAreOverdue = isAutomated && engineOff && totalScheduled > 0

  return (
    <div className="space-y-4">
      <HowItWorks steps={data.steps} cadenceName={data.cadence.name} />

      <TooltipProvider delayDuration={200}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {data.cadence.total_steps} steps · {data.cadence.total_days} días · ventana últimos{' '}
            {data.days_window}d
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {/* CA-055: surface automation_mode — is this cadence automated or manual? */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant={isAutomated ? 'default' : 'secondary'}
                  className="gap-1 text-[10px]"
                >
                  {isAutomated ? <Zap className="size-3" /> : <Hand className="size-3" />}
                  {isAutomated ? 'Automatizada' : 'Manual'}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                {isAutomated
                  ? 'Los pasos se agendan y (con el motor encendido) se ejecutan solos.'
                  : 'Sin ejecución automática: cada paso se envía manualmente desde la cadencia.'}
              </TooltipContent>
            </Tooltip>

            {/* CA-055: distinguish a live queue from a dead one. */}
            {scheduledAreOverdue ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-500/50 text-[10px] text-amber-700 dark:text-amber-400"
                  >
                    <PauseCircle className="size-3" />
                    {totalScheduled} vencido{totalScheduled === 1 ? '' : 's'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Hay {totalScheduled} paso{totalScheduled === 1 ? '' : 's'} agendado
                  {totalScheduled === 1 ? '' : 's'} que no se ejecutará
                  {totalScheduled === 1 ? '' : 'n'} porque el motor de outreach está apagado.
                </TooltipContent>
              </Tooltip>
            ) : isAutomated && engineOn && totalScheduled > 0 ? (
              <Badge
                variant="outline"
                className="gap-1 border-emerald-500/50 text-[10px] text-emerald-700 dark:text-emerald-400"
              >
                <Zap className="size-3" />
                {totalScheduled} en cola
              </Badge>
            ) : null}

            <Badge variant="outline" className="font-mono text-[10px]">
              {data.cadence.status}
            </Badge>
          </div>
        </div>
      </TooltipProvider>

      {data.steps.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Esta cadencia no tiene steps aún.
        </Card>
      ) : (
        <ol className="relative space-y-3">
          {data.steps.map((step, i) => (
            <li key={step.step_id} className="relative">
              <CadenceFlowStepCard step={step} onClick={() => setOpenStepId(step.step_id)} />
              {i < data.steps.length - 1 && (
                <div
                  aria-hidden
                  className="ml-8 h-4 w-px bg-gradient-to-b from-border to-transparent"
                />
              )}
            </li>
          ))}
        </ol>
      )}

      <CadenceFlowStepDialog
        step={openStep}
        open={!!openStepId}
        onOpenChange={(open) => !open && setOpenStepId(null)}
      />
    </div>
  )
}
